import { useState } from 'react';
import clsx from 'clsx';

import { Icon, IconButton, Menu } from '@atom/mui';
import { ArrowDropDownIcon, CloseIcon, HelpIcon } from '@atom/mui/icons';

import { MapDrawingSlice } from '../drawing/drawingTypes';

import {
  AREA_OPTIONS,
  AREA_UNIT_LABELS,
  AreaUnit,
  computeMeasurements,
  convertArea,
  convertDistance,
  DISTANCE_OPTIONS,
  DISTANCE_UNIT_LABELS,
  DistanceUnit,
  formatNumber,
  getEffectiveShape,
  getMessageType,
  MESSAGE_DEFS,
  SHAPE_LABELS,
} from './mapStatsUtilities';

import classes from './mapStats.module.css';

const { MenuItem } = Menu;

type MapStatsProps = {
  drawing: MapDrawingSlice;
};

const MapStats = ({ drawing }: MapStatsProps) => {
  const { activeFeature, mode, onClose, stats } = drawing;
  const itemCounts = stats?.itemCounts;
  const loading = stats?.loading ?? false;

  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>('feet');
  const [radiusUnit, setRadiusUnit] = useState<DistanceUnit>('feet');
  const [areaUnit, setAreaUnit] = useState<AreaUnit>('feet');

  const shape = getEffectiveShape(activeFeature, mode);
  const showLineString = shape === 'linestring';
  const showCircle = shape === 'circle';
  const showArea = shape === 'polygon' || shape === 'circle';

  if (shape === null) {
    return null;
  }

  const label = SHAPE_LABELS[shape];
  const showContent = !loading && activeFeature !== null;

  const { text: messageText, tone: messageTone } =
    MESSAGE_DEFS[
      getMessageType({
        showLineString,
        hasActiveFeature: activeFeature !== null,
        itemCounts,
      })
    ];

  const getMessageStyle = () => {
    switch (messageTone) {
      case 'green':
        return classes.messageGreen;
      case 'orange':
        return classes.messageOrange;
      default:
        return undefined;
    }
  };

  const messageStyle = getMessageStyle();

  const { distanceKm, radiusKm, areaM2 } = computeMeasurements(activeFeature);

  const distanceValue = formatNumber(convertDistance(distanceKm, distanceUnit));
  const radiusValue = formatNumber(convertDistance(radiusKm, radiusUnit));
  const areaValue = formatNumber(convertArea(areaM2, areaUnit));

  const circumferenceLabel = showCircle ? 'Circumference' : 'Perimeter';
  const distanceLabel = showLineString ? 'Distance' : circumferenceLabel;

  const distanceUnitLabel = DISTANCE_UNIT_LABELS[distanceUnit];
  const radiusUnitLabel = DISTANCE_UNIT_LABELS[radiusUnit];
  const areaUnitLabel = AREA_UNIT_LABELS[areaUnit];

  return (
    <div className={classes.container} data-cy="mapStats">
      <div className={classes.header}>
        <div className={classes.titleContent}>
          <span>{label}</span>
          <IconButton
            tooltip="Distance and area values are rounded to the nearest 3 decimal places."
            style={{ padding: 0 }}
          >
            <Icon style={{ fontSize: '1.25rem' }} icon={HelpIcon} />
          </IconButton>
        </div>
        <IconButton onClick={onClose} edge="end">
          <Icon icon={CloseIcon} />
        </IconButton>
      </div>

      {messageText && (
        <div className={clsx(classes.messageRow, messageStyle)}>
          {messageText}
        </div>
      )}

      {showContent && (
        <>
          <div className={classes.contentRow}>
            <div className={classes.rowTitle}>{distanceLabel}</div>
            <div className={classes.rowValue}>
              <span>{`${distanceValue} ${distanceUnitLabel}`}</span>
              <Menu
                icon={<Icon icon={ArrowDropDownIcon} />}
                IconButtonProps={{ size: 'small' }}
              >
                {DISTANCE_OPTIONS.map(opt => (
                  <MenuItem
                    key={opt.value}
                    onClick={() => setDistanceUnit(opt.value)}
                    selected={opt.value === distanceUnit}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
          {showCircle && (
            <div className={classes.contentRow}>
              <div className={classes.rowTitle}>Radius</div>
              <div className={classes.rowValue}>
                <span>{`${radiusValue} ${radiusUnitLabel}`}</span>
                <Menu
                  icon={<Icon icon={ArrowDropDownIcon} />}
                  IconButtonProps={{ size: 'small' }}
                >
                  {DISTANCE_OPTIONS.map(opt => (
                    <MenuItem
                      key={opt.value}
                      onClick={() => setRadiusUnit(opt.value)}
                      selected={opt.value === radiusUnit}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            </div>
          )}
          {showArea && (
            <div className={classes.contentRow}>
              <div className={classes.rowTitle}>Area</div>
              <div className={classes.rowValue}>
                <span>{`${areaValue} ${areaUnitLabel}`}</span>
                <Menu
                  icon={<Icon icon={ArrowDropDownIcon} />}
                  IconButtonProps={{ size: 'small' }}
                >
                  {AREA_OPTIONS.map(opt => (
                    <MenuItem
                      key={opt.value}
                      onClick={() => setAreaUnit(opt.value)}
                      selected={opt.value === areaUnit}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            </div>
          )}
          {showArea && activeFeature !== null && itemCounts && (
            <>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Inventory Items</div>
                <div>{itemCounts.assets}</div>
              </div>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Work</div>
                <div>{itemCounts.workOrders}</div>
              </div>
              <div className={classes.contentRow}>
                <div className={classes.rowTitle}>Team Members</div>
                <div>{itemCounts.users}</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MapStats;
